import {
  ArtifactCanvasBody,
  ArtifactCanvasDismissAction,
  ArtifactCanvasFooterBar,
  ArtifactCanvasSaveActions,
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@yeshuman/sdk'
import type { Organisation } from '@yeshuman/sdk'
import { useMemo, useState } from 'react'

import { JobCreateDocumentCanvas } from './job-create-document-canvas'
import type { OrganisationOpportunity } from '../../hooks/use-organisation-opportunities'

export interface EmployerJobCreateFlowProps {
  organisations: Organisation[]
  /** From `/employer/organisations/:slug/jobs` — skips org picker. */
  lockedOrganisationSlug?: string | null
  /** From job-list organisation filter — pre-selects when the picker is shown. */
  suggestedOrganisationSlug?: string | null
  onClose: () => void
  onCreated: (organisationSlug: string, created: OrganisationOpportunity) => void
}

export function EmployerJobCreateFlow({
  organisations,
  lockedOrganisationSlug,
  suggestedOrganisationSlug,
  onClose,
  onCreated,
}: EmployerJobCreateFlowProps) {
  const locked = lockedOrganisationSlug?.trim() ?? ''

  const needsOrgPick = useMemo(() => {
    if (locked) return false
    return organisations.length > 1
  }, [locked, organisations.length])

  const implicitSlug = useMemo(() => {
    if (locked) return locked
    if (organisations.length === 1) return organisations[0].slug
    return ''
  }, [locked, organisations])

  const suggested = suggestedOrganisationSlug?.trim() ?? ''
  const validSuggested =
    suggested && organisations.some((o) => o.slug === suggested) ? suggested : ''

  const [pickedSlug, setPickedSlug] = useState(validSuggested)
  const [phase, setPhase] = useState<'pick' | 'details'>(() => (needsOrgPick ? 'pick' : 'details'))

  const formSlug = needsOrgPick ? pickedSlug.trim() : implicitSlug

  if (phase === 'pick' && needsOrgPick) {
    return (
      <ArtifactCanvasBody className="flex h-full min-h-0 flex-col space-y-0 p-0">
        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-6">
          <p className="text-sm text-muted-foreground">
            Which organisation is this job for? You cannot change this after the job is posted.
          </p>
          <Label htmlFor="employer-create-job-org">Organisation</Label>
          <Select value={pickedSlug || undefined} onValueChange={setPickedSlug}>
            <SelectTrigger id="employer-create-job-org">
              <SelectValue placeholder="Select organisation" />
            </SelectTrigger>
            <SelectContent>
              {organisations.map((org) => (
                <SelectItem key={org.slug} value={org.slug}>
                  {org.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <ArtifactCanvasFooterBar
          actions={
            <ArtifactCanvasSaveActions
              onClose={onClose}
              onSave={() => setPhase('details')}
              isDirty
              isSaving={false}
              closeLabel="Cancel"
              saveLabel="Continue"
              saveDisabled={!pickedSlug.trim()}
            />
          }
        />
      </ArtifactCanvasBody>
    )
  }

  if (!formSlug) {
    return (
      <ArtifactCanvasBody className="flex h-full min-h-0 flex-col space-y-0 p-0">
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-6">
          <p className="text-sm text-muted-foreground">No organisation available.</p>
        </div>
        <ArtifactCanvasFooterBar actions={<ArtifactCanvasDismissAction onClose={onClose} label="Close" />} />
      </ArtifactCanvasBody>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      {needsOrgPick ? (
        <ArtifactCanvasBody className="shrink-0 space-y-0 border-b border-border">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">
                {organisations.find((o) => o.slug === formSlug)?.name ?? formSlug}
              </p>
              <p className="text-xs text-muted-foreground">Organisation cannot be changed after posting.</p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="shrink-0"
              onClick={() => setPhase('pick')}
            >
              Change
            </Button>
          </div>
        </ArtifactCanvasBody>
      ) : null}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <JobCreateDocumentCanvas
          organisationSlug={formSlug}
          onClose={onClose}
          onCreated={(created) => onCreated(formSlug, created)}
        />
      </div>
    </div>
  )
}
